import { type PasswordChange, passwordChangeSchema } from "@eduleo/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { FullPageLoader } from "../../components/layout/full-page-loader";
import { Button } from "../../components/ui/button";
import { Alert } from "../../components/ui/feedback";
import { PasswordField } from "../../components/ui/password-field";
import { ApiError, describeError } from "../../lib/api-client";
import {
  isPlatformPath,
  isSignInPath,
  SYSTEM_ADMIN_LOGIN_PATH,
  signInPathFor,
} from "../../lib/sign-in-paths";
import { useAuthStore } from "../../stores/auth-store";
import { changePassword, logout } from "./auth-api";
import { CHANGE_PASSWORD_PATH } from "./require-auth";
import { SignInHeroBanner, SignInHeroPanel } from "./sign-in-hero";

type FieldName = "currentPassword" | "newPassword" | "confirmPassword";
const FIELD_ORDER: FieldName[] = ["currentPassword", "newPassword", "confirmPassword"];

/** Where to go afterwards: where they were heading, never back to this page or a sign-in page. */
function destination(state: unknown): string {
  const from = (state as { from?: string } | null)?.from;
  return from && !from.startsWith(CHANGE_PASSWORD_PATH) && !isSignInPath(from) ? from : "/";
}

/**
 * "Choose your own password". Everyone whose password was set by someone else (a new account,
 * a reset, a school's first admin) lands here before anything else; anyone can also come here
 * from the account menu. Built like the sign-in page so it feels like part of signing in.
 */
export function ChangePasswordPage() {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const rememberedSignIn = useAuthStore((state) => state.signInPath);
  const setSession = useAuthStore((state) => state.setSession);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const from = destination(location.state);
  // Eduleo's team signs in on its own page; everyone else on the school sign-in.
  const signInPath = isPlatformPath(from) ? SYSTEM_ADMIN_LOGIN_PATH : rememberedSignIn;

  const [values, setValues] = useState<Record<FieldName, string>>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [signingOut, setSigningOut] = useState(false);
  const inputs = useRef<Partial<Record<FieldName, HTMLInputElement | null>>>({});

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (session) => {
      setSession(session);
      navigate(from, { replace: true });
      // Anything that failed while the password was pending can load now.
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        const next: Partial<Record<FieldName, string>> = {};
        for (const item of error.fieldErrors) {
          const field = item.path as FieldName;
          if (FIELD_ORDER.includes(field) && !next[field]) next[field] = item.message;
        }
        // A wrong current password is cleared so it can be typed again, like at sign-in.
        if (next.currentPassword) setValues((current) => ({ ...current, currentPassword: "" }));
        show(next);
      }
    },
  });

  if (status === "loading") return <FullPageLoader />;
  if (status === "signed_out" || !user) return <Navigate to={signInPath} replace />;
  const forced = user.mustChangePassword;

  function show(next: Partial<Record<FieldName, string>>) {
    setErrors(next);
    const first = FIELD_ORDER.find((field) => next[field]);
    if (first) inputs.current[first]?.focus();
  }

  function update(field: FieldName, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mutation.isPending) return;
    const input: PasswordChange = {
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    };
    const parsed = passwordChangeSchema.safeParse(input);
    const next: Partial<Record<FieldName, string>> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0]) as FieldName;
        if (FIELD_ORDER.includes(field) && !next[field]) next[field] = issue.message;
      }
    }
    // Typing it twice catches a slip the person couldn't see in a hidden field.
    if (!next.newPassword && values.confirmPassword !== values.newPassword) {
      next.confirmPassword = values.confirmPassword
        ? "The two new passwords don't match"
        : "Type the new password again";
    }
    if (Object.keys(next).length > 0 || !parsed.success) return show(next);
    setErrors({});
    mutation.mutate(parsed.data);
  }

  async function signOut() {
    setSigningOut(true);
    const signInPage = signInPathFor(user);
    try {
      await logout();
    } catch {
      // The local session is cleared either way.
    } finally {
      queryClient.clear();
      navigate(signInPage, { replace: true });
    }
  }

  const serverError =
    mutation.error && !(mutation.error instanceof ApiError && mutation.error.fieldErrors.length > 0)
      ? describeError(mutation.error)
      : null;

  const field = (name: FieldName) => ({
    ref: (element: HTMLInputElement | null) => {
      inputs.current[name] = element;
    },
    name,
    value: values[name],
    error: errors[name],
    onChange: (event: { target: { value: string } }) => update(name, event.target.value),
  });

  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[1.1fr_1fr]">
      <SignInHeroPanel />

      <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-6 sm:px-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <SignInHeroBanner />

          <div className="rounded-xl border border-edge bg-surface p-6 shadow-sm sm:p-8">
            <span className="grid size-12 place-items-center rounded-[14px] bg-brand-soft text-brand-fg">
              <KeyRound aria-hidden className="size-6" strokeWidth={1.5} />
            </span>
            <h2 className="mt-4 font-bold text-2xl">
              {forced ? "Choose your own password" : "Change your password"}
            </h2>
            <p className="mt-1 text-ink-2">
              {forced
                ? "Your password was set by someone else. Pick a new one that only you know before you continue."
                : "Pick a new password that only you know."}
            </p>
            <p className="mt-3 rounded-md bg-surface-2 px-3 py-2 text-sm">
              Signed in as <strong>{user.displayName}</strong>
              <span className="block font-mono text-ink-3 text-xs">
                {user.institution.code} / {user.loginId}
              </span>
            </p>

            {serverError && (
              <Alert tone="danger" title={serverError.title} className="mt-5">
                {serverError.detail}
              </Alert>
            )}

            <form
              noValidate
              onSubmit={onSubmit}
              className="mt-6 flex flex-col gap-5"
              aria-label={forced ? "Choose your own password" : "Change your password"}
            >
              {/* Lets password managers save the new password against the right account. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={user.loginId}
                readOnly
                hidden
              />
              <PasswordField
                label={forced ? "Temporary password" : "Current password"}
                hint={forced ? "The password you were given and just used to sign in." : undefined}
                autoComplete="current-password"
                autoFocus
                {...field("currentPassword")}
              />
              <PasswordField
                label="New password"
                hint="At least 10 characters. A short sentence is easy to remember and hard to guess."
                autoComplete="new-password"
                {...field("newPassword")}
              />
              <PasswordField
                label="Type the new password again"
                autoComplete="new-password"
                {...field("confirmPassword")}
              />
              <Button
                type="submit"
                size="lg"
                block
                loading={mutation.isPending}
                loadingText="Saving your password…"
              >
                Save my password
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-ink-2 text-sm">
            Signing in on other phones or computers? You'll need the new password there too.
          </p>
          <div className="mt-2 flex justify-center gap-4">
            {forced ? (
              <Button
                variant="ghost"
                size="sm"
                loading={signingOut}
                loadingText="Signing out…"
                onClick={signOut}
              >
                Sign out
              </Button>
            ) : (
              <Link
                to={from}
                className="font-semibold text-brand-fg text-sm underline-offset-4 hover:underline"
              >
                Cancel
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
